import { useField } from "formik";
import { useEffect, useRef } from "react";

type UseDebounceFieldOptions = {
  skipInitial?: boolean;
  minLength?: number;
};

/**
 * @description - Custom hook to delay some action based on value changed
 * @param {string} fieldName - Value that going to be change
 * @param {Function} callBack - Callback function to execute when the delay passed
 * @param {number} delay - Delay in milliseconds, default is 500ms
 */
const useDebounceField = <T,>(
  fieldName: string,
  callBack: (value: string) => void,
  delay: number = 500,
  options?: UseDebounceFieldOptions,
) => {
  const [, meta] = useField(fieldName);
  const { value } = meta;

  const hasMountedRef = useRef(false);

  const callbackRef = useRef(callBack);
  useEffect(() => {
    callbackRef.current = callBack;
  }, [callBack]);

  useEffect(() => {
    if (options?.skipInitial && !hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    hasMountedRef.current = true;

    const nextValue = (value ?? "").toString();
    const minLength = options?.minLength;
    if (typeof minLength === "number" && minLength > 0) {
      if (nextValue.length > 0 && nextValue.length < minLength) return;
    }

    const timeout = setTimeout(() => {
      callbackRef.current(nextValue);
    }, delay);

    return () => clearTimeout(timeout);
  }, [value, delay, options?.skipInitial, options?.minLength]);

  return value as T;
};

export default useDebounceField;
